import {
  AccessRequest,
  AssignSummary,
  BackupResult,
  ChangeRequest,
  ComputeOptions,
  DirectoryUser,
  GovernanceRules,
  GovernanceSummary,
  GovernanceVm,
  Justification,
  StorageInfo,
  ImportSummary,
  ImportValidationResult,
  VmInstance,
  VmManager,
  VmManagersResponse,
  VmRequest,
} from './types';
import {
  AuditLogEntry,
  Me,
  RbacPermission,
  RbacRole,
  RbacUser,
  UserStatus,
} from './rbacTypes';

type FetchApi = { fetch: typeof globalThis.fetch };

export async function fetchInfrastructureApi<T>(
  fetchApi: FetchApi,
  backendUrl: string,
  path: string,
  options?: RequestInit,
): Promise<T> {
  const response = await fetchApi.fetch(
    `${backendUrl}/api/infrastructure${path}`,
    {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      ...options,
    },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || response.statusText);
  }
  return response.json();
}

// RbacContext (on mount + every window focus) and TopBarUserMenu (see its
// own comment on why it can't just use useRbac()) each call getMe()
// independently — real, load-tested concurrent-session behavior showed
// these landing within milliseconds of each other on every page load,
// each paying the full /me cost (a real RBAC permission computation) for
// what's the same answer. Single-flight: callers that ask while a request
// is already in progress share that same promise instead of firing a
// second one; the next call after it settles starts a fresh request, so
// this never serves stale data past the moment of that one shared fetch.
let inFlightMe: Promise<Me> | null = null;

/** Current caller's identity, role, and effective permissions. */
export async function getMe(
  fetchApi: FetchApi,
  backendUrl: string,
): Promise<Me> {
  if (inFlightMe) return inFlightMe;
  inFlightMe = fetchInfrastructureApi<Me>(fetchApi, backendUrl, '/me').finally(
    () => {
      inFlightMe = null;
    },
  );
  return inFlightMe;
}

export async function listRequests(
  fetchApi: FetchApi,
  backendUrl: string,
  params?: { mine?: boolean; status?: string },
): Promise<VmRequest[]> {
  const query = new URLSearchParams();
  if (params?.mine) query.set('mine', 'true');
  if (params?.status) query.set('status', params.status);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const data = await fetchInfrastructureApi<{ requests: VmRequest[] }>(
    fetchApi,
    backendUrl,
    `/requests${suffix}`,
  );
  return data.requests;
}

export async function listPendingRequests(
  fetchApi: FetchApi,
  backendUrl: string,
): Promise<VmRequest[]> {
  const data = await fetchInfrastructureApi<{ requests: VmRequest[] }>(
    fetchApi,
    backendUrl,
    '/requests/pending',
  );
  return data.requests;
}

/** Approved/rejected/completed requests the caller is authorized to review. */
export async function listApprovalHistory(
  fetchApi: FetchApi,
  backendUrl: string,
): Promise<VmRequest[]> {
  const data = await fetchInfrastructureApi<{ requests: VmRequest[] }>(
    fetchApi,
    backendUrl,
    '/requests/history',
  );
  return data.requests;
}

export async function getRequest(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
): Promise<VmRequest> {
  const data = await fetchInfrastructureApi<{ request: VmRequest }>(
    fetchApi,
    backendUrl,
    `/requests/${id}`,
  );
  return data.request;
}

export async function approveRequest(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
): Promise<VmRequest> {
  const data = await fetchInfrastructureApi<{ request: VmRequest }>(
    fetchApi,
    backendUrl,
    `/requests/${id}/approve`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  return data.request;
}

export async function rejectRequest(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
  reason?: string,
): Promise<VmRequest> {
  const data = await fetchInfrastructureApi<{ request: VmRequest }>(
    fetchApi,
    backendUrl,
    `/requests/${id}/reject`,
    { method: 'POST', body: JSON.stringify({ reason }) },
  );
  return data.request;
}

export async function cancelRequest(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
): Promise<VmRequest> {
  const data = await fetchInfrastructureApi<{ request: VmRequest }>(
    fetchApi,
    backendUrl,
    `/requests/${id}/cancel`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  return data.request;
}

export async function changeRequestApprover(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
  approverEmail: string,
): Promise<VmRequest> {
  const data = await fetchInfrastructureApi<{ request: VmRequest }>(
    fetchApi,
    backendUrl,
    `/requests/${id}/change-approver`,
    { method: 'POST', body: JSON.stringify({ approverEmail }) },
  );
  return data.request;
}

export async function postRequestComment(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
  body: string,
): Promise<VmRequest> {
  const data = await fetchInfrastructureApi<{ request: VmRequest }>(
    fetchApi,
    backendUrl,
    `/requests/${id}/comments`,
    { method: 'POST', body: JSON.stringify({ body }) },
  );
  return data.request;
}

export interface RequesterHistory {
  totalRequests: number;
  approvedCount: number;
  activeVmCount: number;
}

/** The requester's own track record — how many requests they've submitted
 * before, how many were approved, and how many VMs they currently have
 * active. Scoped to whoever can already see the given request (owner,
 * assigned approver, or a super admin). */
export async function getRequesterHistory(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
): Promise<RequesterHistory> {
  return fetchInfrastructureApi<RequesterHistory>(
    fetchApi,
    backendUrl,
    `/requests/${id}/requester-history`,
  );
}

export interface OciLookupResult {
  id: string;
  instanceId: string;
  vmName: string;
  region: string;
  compartmentId: string;
  compartmentName?: string;
  ownerEmail: string;
  powerState: string;
  createdAt: string;
  shape?: string;
  ocpus?: number;
  memoryInGBs?: number;
  publicIp?: string;
  privateIp?: string;
  bootVolumeSizeGb?: number;
  availabilityDomain?: string;
  nsgId?: string;
}

/** Super-admin-only: the network/storage detail the OCI-wide "all VMs" list
 * (see listMyVms scope=all) deliberately omits for the whole list (a
 * per-instance VNIC/boot-volume lookup for 200+ rows would be slow) —
 * fetched only for the one instance a user actually opens the detail page
 * for. See instancesRouter.ts's GET /instances/oci-lookup. */
export async function lookupOciVm(
  fetchApi: FetchApi,
  backendUrl: string,
  vmName: string,
): Promise<OciLookupResult> {
  const data = await fetchInfrastructureApi<{ instance: OciLookupResult }>(
    fetchApi,
    backendUrl,
    `/instances/oci-lookup?vmName=${encodeURIComponent(vmName)}`,
  );
  return data.instance;
}

interface ListVmsResult {
  instances: VmInstance[];
  isAdmin: boolean;
  /** Only present when `page`/`limit` were passed — the full count before
   * slicing, for pagination controls. */
  total?: number;
}

export async function listMyVms(
  fetchApi: FetchApi,
  backendUrl: string,
  scope: 'mine' | 'all' = 'mine',
  pagination?: { page: number; limit: number },
): Promise<ListVmsResult> {
  const query = new URLSearchParams({ scope });
  if (pagination) {
    query.set('page', String(pagination.page));
    query.set('limit', String(pagination.limit));
  }
  return fetchInfrastructureApi<ListVmsResult>(
    fetchApi,
    backendUrl,
    `/instances?${query.toString()}`,
  );
}

export async function refreshVms(
  fetchApi: FetchApi,
  backendUrl: string,
  scope: 'mine' | 'all' = 'mine',
  // Only an explicit user click on the "Refresh" button should pay for a
  // genuinely fresh OCI pull for scope=all — the same endpoint is also
  // polled every 5s while any VM is transitional (see YourVmsPage.tsx),
  // and forcing a live tenancy-wide sweep on every one of those ticks would
  // reintroduce the exact latency the backend cache exists to avoid.
  force = false,
): Promise<ListVmsResult> {
  return fetchInfrastructureApi<ListVmsResult>(
    fetchApi,
    backendUrl,
    '/instances/refresh',
    {
      method: 'POST',
      body: JSON.stringify({ scope, force }),
    },
  );
}

async function performLifecycleAction(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
  action: 'start' | 'stop' | 'reboot' | 'terminate',
  extra?: Record<string, unknown>,
): Promise<VmInstance> {
  const data = await fetchInfrastructureApi<{ instance: VmInstance }>(
    fetchApi,
    backendUrl,
    `/instances/${id}/${action}`,
    {
      method: 'POST',
      body: JSON.stringify({ ...extra }),
    },
  );
  return data.instance;
}

export async function startVm(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
): Promise<VmInstance> {
  return performLifecycleAction(fetchApi, backendUrl, id, 'start');
}

export async function stopVm(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
): Promise<VmInstance> {
  return performLifecycleAction(fetchApi, backendUrl, id, 'stop');
}

export async function rebootVm(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
): Promise<VmInstance> {
  return performLifecycleAction(fetchApi, backendUrl, id, 'reboot');
}

export async function terminateVm(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
  options?: { deleteNsg?: boolean; blockVolumeIdsToDelete?: string[] },
): Promise<VmInstance> {
  return performLifecycleAction(fetchApi, backendUrl, id, 'terminate', options);
}

/**
 * Download the shared SSH private key for an instance. Uses the fetch API (not
 * a plain link) so the Backstage auth token is sent — the endpoint is now
 * authenticated and derives ownership from the session.
 */
export async function downloadSshKey(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
): Promise<void> {
  const response = await fetchApi.fetch(
    `${backendUrl}/api/infrastructure/instances/${id}/ssh-key`,
    { credentials: 'include' },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || 'Failed to download SSH key');
  }
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'backstage-vm-key.pem';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export interface Approver {
  displayName: string;
  email: string;
  role: RbacRole;
}

/** Assignable approvers (admins + super admins). Any authenticated user. */
export async function listApprovers(
  fetchApi: FetchApi,
  backendUrl: string,
): Promise<Approver[]> {
  const data = await fetchInfrastructureApi<{ approvers: Approver[] }>(
    fetchApi,
    backendUrl,
    '/approvers',
  );
  return data.approvers;
}

// --- User management (super admin) ---

export async function listUsers(
  fetchApi: FetchApi,
  backendUrl: string,
  params?: { search?: string; role?: RbacRole; status?: UserStatus },
): Promise<RbacUser[]> {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.role) query.set('role', params.role);
  if (params?.status) query.set('status', params.status);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const data = await fetchInfrastructureApi<{ users: RbacUser[] }>(
    fetchApi,
    backendUrl,
    `/users${suffix}`,
  );
  return data.users;
}

export async function createUser(
  fetchApi: FetchApi,
  backendUrl: string,
  input: {
    email: string;
    displayName?: string;
    role: RbacRole;
    team?: string;
  },
): Promise<RbacUser> {
  const data = await fetchInfrastructureApi<{ user: RbacUser }>(
    fetchApi,
    backendUrl,
    '/users',
    { method: 'POST', body: JSON.stringify(input) },
  );
  return data.user;
}

export async function updateUserTeam(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
  team: string,
): Promise<RbacUser> {
  const data = await fetchInfrastructureApi<{ user: RbacUser }>(
    fetchApi,
    backendUrl,
    `/users/${id}/team`,
    { method: 'PATCH', body: JSON.stringify({ team }) },
  );
  return data.user;
}

export async function updateUserRole(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
  role: RbacRole,
): Promise<RbacUser> {
  const data = await fetchInfrastructureApi<{ user: RbacUser }>(
    fetchApi,
    backendUrl,
    `/users/${id}/role`,
    { method: 'PATCH', body: JSON.stringify({ role }) },
  );
  return data.user;
}

export async function updateUserStatus(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
  status: UserStatus,
): Promise<RbacUser> {
  const data = await fetchInfrastructureApi<{ user: RbacUser }>(
    fetchApi,
    backendUrl,
    `/users/${id}/status`,
    { method: 'PATCH', body: JSON.stringify({ status }) },
  );
  return data.user;
}

export async function deleteUser(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
): Promise<void> {
  await fetchInfrastructureApi<{ success: boolean }>(
    fetchApi,
    backendUrl,
    `/users/${id}`,
    { method: 'DELETE' },
  );
}

export async function getUserPermissions(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
): Promise<RbacPermission[]> {
  const data = await fetchInfrastructureApi<{ permissions: RbacPermission[] }>(
    fetchApi,
    backendUrl,
    `/users/${id}/permissions`,
  );
  return data.permissions;
}

export async function updateUserPermission(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
  permission: RbacPermission,
  granted: boolean,
): Promise<{ user: RbacUser; permissions: RbacPermission[] }> {
  return fetchInfrastructureApi<{
    user: RbacUser;
    permissions: RbacPermission[];
  }>(fetchApi, backendUrl, `/users/${id}/permissions`, {
    method: 'PATCH',
    body: JSON.stringify({ permission, granted }),
  });
}

export async function validateImportVm(
  fetchApi: FetchApi,
  backendUrl: string,
  value: string,
): Promise<ImportValidationResult> {
  return fetchInfrastructureApi<ImportValidationResult>(
    fetchApi,
    backendUrl,
    '/instances/import/validate',
    { method: 'POST', body: JSON.stringify({ value }) },
  );
}

export async function validateImportVmBulk(
  fetchApi: FetchApi,
  backendUrl: string,
  identifiers: string[],
): Promise<ImportValidationResult[]> {
  const data = await fetchInfrastructureApi<{
    results: ImportValidationResult[];
  }>(fetchApi, backendUrl, '/instances/import/validate-bulk', {
    method: 'POST',
    body: JSON.stringify({ identifiers }),
  });
  return data.results;
}

export async function importVms(
  fetchApi: FetchApi,
  backendUrl: string,
  identifiers: string[],
): Promise<ImportSummary> {
  return fetchInfrastructureApi<ImportSummary>(
    fetchApi,
    backendUrl,
    '/instances/import',
    {
      method: 'POST',
      body: JSON.stringify({
        identifiers: identifiers.map(value => ({ value })),
      }),
    },
  );
}

export async function listAudit(
  fetchApi: FetchApi,
  backendUrl: string,
  params?: { action?: string; limit?: number },
): Promise<AuditLogEntry[]> {
  const query = new URLSearchParams();
  if (params?.action) query.set('action', params.action);
  if (params?.limit) query.set('limit', String(params.limit));
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const data = await fetchInfrastructureApi<{ entries: AuditLogEntry[] }>(
    fetchApi,
    backendUrl,
    `/audit${suffix}`,
  );
  return data.entries;
}

export interface CostOverviewResult {
  /** 'unavailable' (never a fabricated $0) when Postgres-backed cost
   * ingestion isn't configured, or hasn't produced any data for the
   * requested period yet. */
  status: 'ok' | 'unavailable';
  currency: string;
  lastIngestedAt: string | null;
  dataFreshnessHours: number | null;
  series: { date: string; totalUsd: number }[];
  todayTotalUsd: number | null;
  categoryBreakdown: { category: string; costUsd: number }[];
  topContributors: { vmName: string; costUsd: number }[];
  monthlyBudgetUsd?: number;
}

function toDateParam(date: Date): string {
  return date.toISOString().slice(0, 10);
}

// Actual OCI billing cost (OCI Usage API), not an estimate — see the
// backend's ociCostIngestion.ts. Scoped the same way every other
// Dashboard card already is: a super admin sees the tenancy-wide total,
// everyone else sees only cost attributable to their own VMs.
export async function getCostOverview(
  fetchApi: FetchApi,
  backendUrl: string,
  range: { from: Date; to: Date },
): Promise<CostOverviewResult> {
  const query = new URLSearchParams({
    from: toDateParam(range.from),
    to: toDateParam(range.to),
  });
  return fetchInfrastructureApi<CostOverviewResult>(
    fetchApi,
    backendUrl,
    `/cost-overview?${query.toString()}`,
  );
}

export async function listDirectoryUsers(
  fetchApi: FetchApi,
  backendUrl: string,
): Promise<DirectoryUser[]> {
  const data = await fetchInfrastructureApi<{ users: DirectoryUser[] }>(
    fetchApi,
    backendUrl,
    '/directory/users',
  );
  return data.users;
}

export async function getVmManagers(
  fetchApi: FetchApi,
  backendUrl: string,
  instanceId: string,
): Promise<VmManagersResponse> {
  return fetchInfrastructureApi<VmManagersResponse>(
    fetchApi,
    backendUrl,
    `/instances/${encodeURIComponent(instanceId)}/managers`,
  );
}

export async function assignVmManagers(
  fetchApi: FetchApi,
  backendUrl: string,
  instanceId: string,
  emails: string[],
): Promise<{
  added: VmManager[];
  skipped: { email: string; reason: string }[];
  managers: VmManager[];
}> {
  return fetchInfrastructureApi(
    fetchApi,
    backendUrl,
    `/instances/${encodeURIComponent(instanceId)}/managers`,
    { method: 'POST', body: JSON.stringify({ emails }) },
  );
}

export async function removeVmManager(
  fetchApi: FetchApi,
  backendUrl: string,
  instanceId: string,
  userRef: string,
): Promise<VmManager[]> {
  const data = await fetchInfrastructureApi<{ managers: VmManager[] }>(
    fetchApi,
    backendUrl,
    `/instances/${encodeURIComponent(
      instanceId,
    )}/managers?userRef=${encodeURIComponent(userRef)}`,
    { method: 'DELETE' },
  );
  return data.managers;
}

export async function requestVmAccess(
  fetchApi: FetchApi,
  backendUrl: string,
  instanceId: string,
  message?: string,
): Promise<AccessRequest> {
  const data = await fetchInfrastructureApi<{ request: AccessRequest }>(
    fetchApi,
    backendUrl,
    `/instances/${encodeURIComponent(instanceId)}/access-requests`,
    { method: 'POST', body: JSON.stringify({ message }) },
  );
  return data.request;
}

export async function listAccessRequests(
  fetchApi: FetchApi,
  backendUrl: string,
  scope: 'mine' | 'approving' | 'all',
): Promise<AccessRequest[]> {
  const data = await fetchInfrastructureApi<{ requests: AccessRequest[] }>(
    fetchApi,
    backendUrl,
    `/access-requests?scope=${scope}`,
  );
  return data.requests;
}

export async function getAccessRequest(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
): Promise<{
  request: AccessRequest;
  instance?: {
    id: string;
    vmName: string;
    region: string;
    shape?: string;
    ocpus?: number;
    memoryInGBs?: number;
    powerState: string;
  };
  canDecide: boolean;
}> {
  return fetchInfrastructureApi(
    fetchApi,
    backendUrl,
    `/access-requests/${encodeURIComponent(id)}`,
  );
}

export async function decideAccessRequest(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
  action: 'approve' | 'reject' | 'cancel',
  comment?: string,
): Promise<AccessRequest> {
  const data = await fetchInfrastructureApi<{ request: AccessRequest }>(
    fetchApi,
    backendUrl,
    `/access-requests/${encodeURIComponent(id)}/${action}`,
    { method: 'POST', body: JSON.stringify({ comment }) },
  );
  return data.request;
}

export async function assignVms(
  fetchApi: FetchApi,
  backendUrl: string,
  instanceIds: string[],
  ownerEmail: string | undefined,
  managerEmails: string[],
): Promise<AssignSummary> {
  return fetchInfrastructureApi<AssignSummary>(
    fetchApi,
    backendUrl,
    '/instances/assign',
    {
      method: 'POST',
      body: JSON.stringify({ instanceIds, ownerEmail, managerEmails }),
    },
  );
}

// ---- Compute changes / storage -------------------------------------------

const instancePath = (id: string) => `/instances/${encodeURIComponent(id)}`;

export async function getComputeOptions(
  fetchApi: FetchApi,
  backendUrl: string,
  instanceId: string,
): Promise<ComputeOptions> {
  return fetchInfrastructureApi(
    fetchApi,
    backendUrl,
    `${instancePath(instanceId)}/compute-options`,
  );
}

export async function requestComputeChange(
  fetchApi: FetchApi,
  backendUrl: string,
  instanceId: string,
  body: {
    shape: string;
    ocpus?: number;
    memoryInGBs?: number;
    reason: string;
    approverEmail?: string;
  },
): Promise<ChangeRequest> {
  const data = await fetchInfrastructureApi<{ request: ChangeRequest }>(
    fetchApi,
    backendUrl,
    `${instancePath(instanceId)}/compute-changes`,
    { method: 'POST', body: JSON.stringify(body) },
  );
  return data.request;
}

export async function listInstanceChanges(
  fetchApi: FetchApi,
  backendUrl: string,
  instanceId: string,
): Promise<ChangeRequest[]> {
  const data = await fetchInfrastructureApi<{ requests: ChangeRequest[] }>(
    fetchApi,
    backendUrl,
    `${instancePath(instanceId)}/compute-changes`,
  );
  return data.requests;
}

export async function listChangeRequests(
  fetchApi: FetchApi,
  backendUrl: string,
  scope: 'mine' | 'approving' | 'all',
): Promise<ChangeRequest[]> {
  const data = await fetchInfrastructureApi<{ requests: ChangeRequest[] }>(
    fetchApi,
    backendUrl,
    `/compute-changes?scope=${scope}`,
  );
  return data.requests;
}

export async function getChangeRequest(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
): Promise<{
  request: ChangeRequest;
  instance?: { id: string; vmName: string; powerState: string };
  canDecide: boolean;
  canCancel: boolean;
}> {
  return fetchInfrastructureApi(
    fetchApi,
    backendUrl,
    `/compute-changes/${encodeURIComponent(id)}`,
  );
}

export async function decideChangeRequest(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
  action: 'approve' | 'reject' | 'cancel',
  comment?: string,
): Promise<ChangeRequest> {
  const data = await fetchInfrastructureApi<{ request: ChangeRequest }>(
    fetchApi,
    backendUrl,
    `/compute-changes/${encodeURIComponent(id)}/${action}`,
    { method: 'POST', body: JSON.stringify({ comment }) },
  );
  return data.request;
}

export async function getStorageInfo(
  fetchApi: FetchApi,
  backendUrl: string,
  instanceId: string,
  includeAttachable = false,
): Promise<StorageInfo> {
  return fetchInfrastructureApi(
    fetchApi,
    backendUrl,
    `${instancePath(instanceId)}/storage${
      includeAttachable ? '?include=attachable' : ''
    }`,
  );
}

export async function expandBootVolume(
  fetchApi: FetchApi,
  backendUrl: string,
  instanceId: string,
  sizeGb: number,
): Promise<VmInstance> {
  const data = await fetchInfrastructureApi<{ instance: VmInstance }>(
    fetchApi,
    backendUrl,
    `${instancePath(instanceId)}/storage/expand-boot`,
    { method: 'POST', body: JSON.stringify({ sizeGb }) },
  );
  return data.instance;
}

export async function attachVolume(
  fetchApi: FetchApi,
  backendUrl: string,
  instanceId: string,
  body:
    | { mode: 'new'; name: string; sizeGb: number; vpusPerGb: number }
    | { mode: 'existing'; volumeId: string },
): Promise<VmInstance> {
  const data = await fetchInfrastructureApi<{ instance: VmInstance }>(
    fetchApi,
    backendUrl,
    `${instancePath(instanceId)}/storage/attach`,
    { method: 'POST', body: JSON.stringify(body) },
  );
  return data.instance;
}

export async function detachVolume(
  fetchApi: FetchApi,
  backendUrl: string,
  instanceId: string,
  attachmentId: string,
): Promise<VmInstance> {
  const data = await fetchInfrastructureApi<{ instance: VmInstance }>(
    fetchApi,
    backendUrl,
    `${instancePath(instanceId)}/storage/detach`,
    { method: 'POST', body: JSON.stringify({ attachmentId }) },
  );
  return data.instance;
}

export async function createBackup(
  fetchApi: FetchApi,
  backendUrl: string,
  instanceId: string,
  targets: string[],
): Promise<BackupResult[]> {
  const data = await fetchInfrastructureApi<{ backups: BackupResult[] }>(
    fetchApi,
    backendUrl,
    `${instancePath(instanceId)}/storage/backup`,
    { method: 'POST', body: JSON.stringify({ targets }) },
  );
  return data.backups;
}

// ---- VM Governance --------------------------------------------------------

export async function getGovernanceSummary(
  fetchApi: FetchApi,
  backendUrl: string,
): Promise<GovernanceSummary> {
  return fetchInfrastructureApi(fetchApi, backendUrl, '/governance/summary');
}

export async function listGovernanceVms(
  fetchApi: FetchApi,
  backendUrl: string,
  params: {
    search?: string;
    owner?: string;
    status?: string;
    shape?: string;
    region?: string;
    justification?: string;
    page: number;
    limit: number;
  },
): Promise<{
  vms: GovernanceVm[];
  total: number;
  filterOptions: { shapes: string[]; regions: string[]; teams: string[] };
}> {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '') query.set(k, String(v));
  });
  return fetchInfrastructureApi(
    fetchApi,
    backendUrl,
    `/governance/vms?${query.toString()}`,
  );
}

/** Ask about specific VMs, or about every VM the named owners have. */
export async function requestJustifications(
  fetchApi: FetchApi,
  backendUrl: string,
  target: { instanceIds: string[] } | { ownerEmails: string[] },
  message: string,
): Promise<{
  created: Justification[];
  skipped: { instanceId: string; vmName?: string; reason: string }[];
}> {
  return fetchInfrastructureApi(
    fetchApi,
    backendUrl,
    '/governance/justifications',
    { method: 'POST', body: JSON.stringify({ ...target, message }) },
  );
}

export async function listJustifications(
  fetchApi: FetchApi,
  backendUrl: string,
  scope: 'mine' | 'all',
): Promise<Justification[]> {
  const data = await fetchInfrastructureApi<{
    justifications: Justification[];
  }>(fetchApi, backendUrl, `/justifications?scope=${scope}`);
  return data.justifications;
}

export async function getJustification(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
): Promise<{
  justification: Justification;
  instance?: {
    id: string;
    vmName: string;
    shape?: string;
    ocpus?: number;
    memoryInGBs?: number;
    powerState: string;
    region: string;
  };
  canRespond: boolean;
  canReview: boolean;
}> {
  return fetchInfrastructureApi(
    fetchApi,
    backendUrl,
    `/justifications/${encodeURIComponent(id)}`,
  );
}

export async function respondToJustification(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
  response: string,
): Promise<Justification> {
  const data = await fetchInfrastructureApi<{ justification: Justification }>(
    fetchApi,
    backendUrl,
    `/justifications/${encodeURIComponent(id)}/respond`,
    { method: 'POST', body: JSON.stringify({ response }) },
  );
  return data.justification;
}

export async function reviewJustification(
  fetchApi: FetchApi,
  backendUrl: string,
  id: string,
  outcome: 'accepted' | 'declined',
  comment?: string,
): Promise<Justification> {
  const data = await fetchInfrastructureApi<{ justification: Justification }>(
    fetchApi,
    backendUrl,
    `/governance/justifications/${encodeURIComponent(id)}/review`,
    { method: 'POST', body: JSON.stringify({ outcome, comment }) },
  );
  return data.justification;
}

export async function getGovernanceRules(
  fetchApi: FetchApi,
  backendUrl: string,
): Promise<GovernanceRules> {
  const data = await fetchInfrastructureApi<{ rules: GovernanceRules }>(
    fetchApi,
    backendUrl,
    '/governance/rules',
  );
  return data.rules;
}

export async function updateGovernanceRules(
  fetchApi: FetchApi,
  backendUrl: string,
  rules: Pick<
    GovernanceRules,
    'flagEnabled' | 'flagAfterDays' | 'defaultMessage'
  >,
): Promise<GovernanceRules> {
  const data = await fetchInfrastructureApi<{ rules: GovernanceRules }>(
    fetchApi,
    backendUrl,
    '/governance/rules',
    { method: 'PUT', body: JSON.stringify(rules) },
  );
  return data.rules;
}

export async function notifyGovernanceUsers(
  fetchApi: FetchApi,
  backendUrl: string,
  emails: string[],
  message: string,
): Promise<{ sent: number; skipped: string[] }> {
  return fetchInfrastructureApi(fetchApi, backendUrl, '/governance/notify', {
    method: 'POST',
    body: JSON.stringify({ emails, message }),
  });
}

/** Downloads the governance VM list as CSV (authenticated, so not a plain link). */
export async function downloadGovernanceCsv(
  fetchApi: FetchApi,
  backendUrl: string,
): Promise<void> {
  const response = await fetchApi.fetch(
    `${backendUrl}/api/infrastructure/governance/export.csv`,
    { credentials: 'include' },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || 'Failed to export');
  }
  const url = window.URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = 'vm-governance.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
